import { useEffect, useState } from "react";

/**
 * Plays a vault-unlock loading splash on first Dashboard mount per session.
 * - Padlock wobbles, a rotating quip fades in below it, 3 dots pulse to show
 *   it's actually doing something.
 * - Pure auto-play loading animation — no click required. Auto-advances
 *   through shaking → unlocked → gone (~2.9s total). Honours reduced-motion.
 * - Click is a quiet escape hatch only; we don't advertise it.
 *
 * IMPORTANT: sessionStorage gating happens in the useState initializer (which
 * only runs once per real mount), NOT inside the useEffect — so React.StrictMode
 * dev double-invoke doesn't lose our setTimeouts.
 */

const QUIPS = [
  "warming up the keymaker…",
  "polishing your achievements…",
  "alphabetising your existential dread…",
  "translating your apprenticeship into HR…",
  "muting the recruiter spam…",
  "decanting your CV like fine wine…",
  "drafting an excuse for the employment gap…",
  "filing down a rough bullet point…",
  "wiping a smudge off your reference list…",
  "reminding the ATS that you're not a robot…",
];

function pickInitialStage() {
  if (typeof window === "undefined") return "hidden";
  try {
    if (sessionStorage.getItem("vault_unlocked_once") === "1") return "hidden";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      sessionStorage.setItem("vault_unlocked_once", "1");
      return "hidden";
    }
    sessionStorage.setItem("vault_unlocked_once", "1");
    return "shaking";
  } catch {
    return "hidden";
  }
}

export default function VaultUnlock() {
  const [stage, setStage] = useState(pickInitialStage); // hidden | shaking | unlocked | gone
  const [quip] = useState(() => QUIPS[Math.floor(Math.random() * QUIPS.length)]);

  const skip = () => setStage("unlocked");

  useEffect(() => {
    if (stage === "shaking") {
      const t1 = setTimeout(() => setStage("unlocked"), 1400);
      const t2 = setTimeout(() => setStage("gone"),     2400);
      const t3 = setTimeout(() => setStage("hidden"),   3000);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
    if (stage === "unlocked") {
      const t1 = setTimeout(() => setStage("gone"),   1000);
      const t2 = setTimeout(() => setStage("hidden"), 1600);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (stage === "gone") {
      const t = setTimeout(() => setStage("hidden"), 600);
      return () => clearTimeout(t);
    }
  }, [stage]);

  if (stage === "hidden") return null;

  return (
    <div className={`vault-unlock-overlay ${stage}`} aria-hidden="true" data-testid="vault-unlock">
      <div className="vault-unlock-stage">
        <button type="button"
          className="vault-unlock-lock-btn"
          onClick={skip}
          aria-label="skip vault unlock animation"
          data-testid="vault-unlock-skip"
          tabIndex={-1}>
          <svg viewBox="-20 -50 240 320" width="200" height="266"
            overflow="visible"
            className="vault-unlock-lock"
            xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* Cobalt band gradient — matches the brand mark */}
              <linearGradient id="vu-band" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#4A6BFF"/>
                <stop offset="100%" stopColor="#1B41F8"/>
              </linearGradient>
              {/* Soft pale-cobalt middle door */}
              <linearGradient id="vu-mid" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#EAEEFF"/>
                <stop offset="100%" stopColor="#D0D8FF"/>
              </linearGradient>
              {/* Soft ambient floor shadow */}
              <radialGradient id="vu-floor-shadow" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#121212" stopOpacity="0.32"/>
                <stop offset="100%" stopColor="#121212" stopOpacity="0"/>
              </radialGradient>
            </defs>

            {/* Floor shadow */}
            <ellipse cx="100" cy="244" rx="80" ry="9" fill="url(#vu-floor-shadow)"/>

            {/* Grip handle on top */}
            <rect x="76" y="-4" width="48" height="26" rx="7"
              fill="none" stroke="#0A0A0A" strokeWidth="6" strokeLinejoin="round"/>

            {/* Vault body */}
            <rect x="14" y="22" width="172" height="200" rx="14"
              fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="7" strokeLinejoin="round"/>

            {/* Top band */}
            <rect x="28" y="40" width="144" height="26" rx="3"
              fill="url(#vu-band)" stroke="#0A0A0A" strokeWidth="4"/>

            {/* Bottom band */}
            <rect x="28" y="178" width="144" height="26" rx="3"
              fill="url(#vu-band)" stroke="#0A0A0A" strokeWidth="4"/>

            {/* Middle door */}
            <rect x="28" y="74" width="144" height="96" rx="5"
              fill="url(#vu-mid)" stroke="#0A0A0A" strokeWidth="4"/>

            {/* Central combination dial */}
            <g transform="translate(100 122)">
              <circle cx="0" cy="0" r="24" fill="#FFFFFF" stroke="#0A0A0A" strokeWidth="4"/>
              {/* 12 tick marks */}
              {[...Array(12)].map((_, i) => {
                const a = (i * 30 - 90) * (Math.PI / 180);
                const x1 = Math.cos(a) * 18, y1 = Math.sin(a) * 18;
                const x2 = Math.cos(a) * 22, y2 = Math.sin(a) * 22;
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0A0A0A" strokeWidth="1.6"/>;
              })}
              {/* Centre dot */}
              <circle cx="0" cy="0" r="6" fill="#1B41F8" stroke="#0A0A0A" strokeWidth="2"/>
              {/* Pointer at 12 o'clock — spins during the shake stage */}
              <rect x="-1.4" y="-18" width="2.8" height="9" fill="#0A0A0A" className="vault-unlock-pointer"/>
            </g>

            {/* Sage 'secure' status LED — sits to the bottom-right of the dial */}
            <g transform="translate(154 190)">
              <circle cx="0" cy="0" r="6" fill="#7FA17F"/>
              <circle cx="0" cy="0" r="6" fill="none" stroke="#0A0A0A" strokeWidth="1.5"/>
              <circle cx="-1.6" cy="-1.6" r="1.6" fill="#FFFFFF" fillOpacity="0.7"/>
            </g>

            {/* Sparkles emitted during unlock */}
            <g className="vault-sparkles">
              {[...Array(6)].map((_, i) => (
                <circle key={i} cx="100" cy="20"
                  r={2 + (i % 3)}
                  fill={["#1B41F8","#1B41F8","#7FA17F","#FF5C39","#7C4DC3","#1B41F8"][i % 6]}
                  style={{ "--i": i }}/>
              ))}
            </g>
          </svg>
        </button>

        <div className="vault-unlock-progress" aria-hidden="true">
          <span/><span/><span/>
        </div>

        <p className="vault-unlock-quip" data-testid="vault-unlock-quip">{quip}</p>

        {stage === "shaking" && (
          <p className="vault-unlock-hint" data-testid="vault-unlock-loading">unlocking your vault…</p>
        )}
        {stage !== "shaking" && (
          <p className="vault-unlock-tag">JobVault unlocked.</p>
        )}
      </div>
    </div>
  );
}
